import { useEffect, useId, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import {
  AD_UNITS,
  adPlatform,
  initAdsNative,
  adSuppressionReason,
  reportAdEvent,
} from "@/lib/ads";
import { InlineBanner, type InlineBannerGeometry } from "@/lib/inline-banner";
import { isAnyOverlayOpen } from "@/lib/overlay-visibility";

/**
 * In-feed ad slot — drop it into a list like any other row.
 *
 * On Android the ad is a real AdMob inline adaptive banner (native view)
 * placed over this element by the app-local InlineBanner plugin. The plugin
 * moves it with the WebView's scroll every frame, so it scrolls with the list
 * like a row, and clips it under the sticky header / bottom bar
 * (`data-ad-clip="top" | "bottom"` elements).
 *
 * This element only reserves the space: a quiet card-shaped skeleton while the
 * ad loads, then the banner's exact height. No "Sponsored" strip — the banner
 * carries its own AdChoices badge. On no-fill the slot collapses entirely.
 *
 * Ads never interrupt the UI. Because the banner is a native view it paints
 * ABOVE every DOM element, so it steps aside (hidden, not reloaded) while:
 *   • a dialog / sheet is open,
 *   • any floating layer overlaps it — menus, popovers, toasts, the FAB, the
 *     tour popover, install/update prompts, or anything marked `data-ad-avoid`,
 *   • the keyboard is up (an input / textarea / contenteditable has focus),
 *   • a pull-to-refresh gesture or refresh is in progress.
 *
 * Web / iOS render nothing (no AdSense / iOS AdMob wiring yet).
 */

// `trip` was `timeline` in earlier releases — it moved out of the
// per-tab Timeline route and into the shared trip layout under the
// hero, so the slot now shows on every trip tab.
export type AdSlotName = "dashboard" | "trip";

/**
 * Floating DOM layers the banner must never cover. Anything new that floats
 * (fixed/absolute over content) should carry `data-ad-avoid` or one of these
 * roles.
 */
const AVOID_SELECTOR = [
  "[data-ad-avoid]",
  "[data-sonner-toast]",
  '[role="menu"]',
  '[role="listbox"]',
  '[role="tooltip"]',
  "[data-radix-popper-content-wrapper]",
  ".driver-popover",
].join(",");

/** Height reserved while the ad request is in flight. */
const LOADING_HEIGHT_PX = 60;

interface InlineAdProps {
  slot: AdSlotName;
  /**
   * Tallest ad (dp) AdMob may return. Inline adaptive banners pick a height
   * up to this; bigger = more fill / revenue, but more of the screen.
   */
  maxHeight?: number;
  className?: string;
}

type Status = "loading" | "loaded" | "failed";

export function InlineAd({ slot, maxHeight = 100, className }: InlineAdProps) {
  const ref = useRef<HTMLDivElement>(null);
  const bannerId = `${slot}-${useId()}`;
  const [suppressed] = useState(() =>
    adPlatform() === "android-native" ? adSuppressionReason(slot) : "platform",
  );
  const enabled = suppressed === null;
  const [status, setStatus] = useState<Status>("loading");
  const [height, setHeight] = useState(LOADING_HEIGHT_PX);

  useEffect(() => {
    if (suppressed && suppressed !== "platform") {
      reportAdEvent("ad_slot_suppressed", { slot, reason: suppressed });
    }
  }, [suppressed, slot]);

  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;

    let disposed = false;
    let created = false;
    let raf = 0;
    let last: InlineBannerGeometry | null = null;
    let shown = true;
    const handles: Array<Promise<{ remove: () => Promise<void> }>> = [];
    let safeTop = readSafeTop();
    const onSafeArea = () => {
      safeTop = readSafeTop();
    };
    window.addEventListener("travelix:safe-area", onSafeArea);

    const measure = (): InlineBannerGeometry & { width: number } => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      let clipTop = safeTop;
      let clipBottom = 0;
      document.querySelectorAll<HTMLElement>("[data-ad-clip]").forEach((bar) => {
        const r = bar.getBoundingClientRect();
        if (r.height === 0) return;
        if (bar.dataset.adClip === "top") clipTop = Math.max(clipTop, r.bottom);
        else clipBottom = Math.max(clipBottom, vh - r.top);
      });
      return {
        x: rect.left,
        y: rect.top + window.scrollY,
        width: rect.width,
        clipTop,
        clipBottom,
      };
    };

    // Layout changes (filters, images loading, pull-to-refresh, a sticky
    // header resizing) move the placeholder without a scroll. Check once per
    // frame and only cross the bridge when something actually moved —
    // plain scrolling leaves every value unchanged, native handles that.
    const tick = () => {
      if (disposed) return;
      raf = requestAnimationFrame(tick);
      if (!created) return;
      const g = measure();
      const show = !shouldYield(el, g);
      if (show !== shown) {
        shown = show;
        void InlineBanner.setVisible({ id: bannerId, visible: show });
      }
      if (
        last &&
        Math.abs(g.x - last.x) < 0.5 &&
        Math.abs(g.y - last.y) < 0.5 &&
        Math.abs(g.clipTop - last.clipTop) < 0.5 &&
        Math.abs(g.clipBottom - last.clipBottom) < 0.5
      ) {
        return;
      }
      last = g;
      void InlineBanner.update({ id: bannerId, ...g });
    };

    handles.push(
      InlineBanner.addListener("bannerLoaded", (e) => {
        if (e.id !== bannerId) return;
        if (e.height > 0) setHeight(e.height);
        setStatus("loaded");
        reportAdEvent("ad_loaded", { slot, height: e.height });
      }),
      InlineBanner.addListener("bannerFailed", (e) => {
        if (e.id !== bannerId) return;
        reportAdEvent("ad_request_failed", {
          slot,
          code: e.code,
          message: String(e.message).slice(0, 100),
        });
        setStatus("failed");
      }),
    );

    void (async () => {
      // AdMob.initialize() (kicked off in capacitor-glue) must resolve before
      // the first request, or the SDK silently drops it.
      await initAdsNative();
      if (disposed) return;
      const g = measure();
      last = g;
      const adId = AD_UNITS.banners[slot];
      if (import.meta.env.DEV) {
        console.info(`[ads] requesting inline banner: slot=${slot} adId=${adId}`, g);
      }
      try {
        await InlineBanner.create({ id: bannerId, adId, maxHeight, ...g });
        created = true;
        if (disposed) {
          void InlineBanner.remove({ id: bannerId });
          return;
        }
        if (shouldYield(el, measure())) {
          shown = false;
          void InlineBanner.setVisible({ id: bannerId, visible: false });
        }
      } catch (err) {
        // e.g. "InlineBanner" plugin not implemented on this build.
        reportAdEvent("ad_request_failed", {
          slot,
          code: -1,
          message: String(err instanceof Error ? err.message : err).slice(0, 100),
        });
        setStatus("failed");
      }
    })();
    raf = requestAnimationFrame(tick);

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      window.removeEventListener("travelix:safe-area", onSafeArea);
      handles.forEach((h) => void h.then((x) => x.remove()));
      if (created) void InlineBanner.remove({ id: bannerId });
    };
  }, [enabled, slot, bannerId, maxHeight]);

  if (!enabled || status === "failed") return null;

  return (
    <div
      ref={ref}
      aria-hidden
      data-ad-slot={slot}
      className={cn(
        "w-full overflow-hidden rounded-lg transition-[height] duration-200",
        status === "loading" && "animate-pulse bg-bg-sunken",
        className,
      )}
      style={{ height }}
    />
  );
}

/**
 * True when the banner must step aside so it never covers or competes with
 * the UI (see the component doc). Runs every frame — keep it cheap.
 */
function shouldYield(el: HTMLElement, g: InlineBannerGeometry): boolean {
  if (isAnyOverlayOpen()) return true;
  if (isEditing()) return true;
  if (document.querySelector("[data-ptr-active]")) return true;

  // The part of the banner actually on screen (it's clipped to the band
  // between the sticky header and the bottom bar).
  const r = el.getBoundingClientRect();
  const top = Math.max(r.top, g.clipTop);
  const bottom = Math.min(r.bottom, window.innerHeight - g.clipBottom);
  if (bottom <= top || r.width === 0) return false;

  for (const layer of document.querySelectorAll<HTMLElement>(AVOID_SELECTOR)) {
    if (layer === el || el.contains(layer)) continue;
    const l = layer.getBoundingClientRect();
    if (l.width === 0 || l.height === 0) continue;
    if (l.left < r.right && l.right > r.left && l.top < bottom && l.bottom > top) {
      return true;
    }
  }
  return false;
}

/** Keyboard is (or is about to be) up: something editable has focus. */
function isEditing(): boolean {
  const a = document.activeElement as HTMLElement | null;
  if (!a) return false;
  if (a.isContentEditable || a.tagName === "TEXTAREA" || a.tagName === "SELECT") return true;
  if (a.tagName !== "INPUT") return false;
  const type = (a as HTMLInputElement).type;
  return !["button", "submit", "reset", "checkbox", "radio", "range", "color", "file"].includes(type);
}

function readSafeTop(): number {
  const v = getComputedStyle(document.documentElement).getPropertyValue("--safe-top");
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}
